import { useTheme } from "next-themes";
import { forwardRef, useEffect, useRef, useState } from "react";
import { categoryButton, fundButton, tabButton } from "./customStyles";
import { Tooltip } from "react-tooltip";
import { useTranslation } from "react-i18next";
import { useI18n } from "../hooks/useI18n";
import { separateNumberIfDecimal } from "../utils/numbers";

function getNormalizedScroll(el) {
  if (!el) return { normalized: 0, max: 0 };

  const scrollLeft = el.scrollLeft;
  const max = Math.max(0, el.scrollWidth - el.clientWidth);
  const dir = getComputedStyle(el).direction; // 'ltr' or 'rtl'

  if (dir !== "rtl") {
    return { normalized: Math.max(0, scrollLeft), max };
  }

  if (scrollLeft < 0) {
    return { normalized: Math.abs(scrollLeft), max };
  } else {
    return { normalized: Math.max(0, max - scrollLeft), max };
  }
}

const FundSelector = forwardRef(({ title, data, updateData, filterItems, items, onItemSelect, showLabel = true }, ref) => {
  const [showSearchInput, setShowSearchInput] = useState(false);
  const [topScrollLeft, setTopScrollLeft] = useState(false);
  const [topScrollRight, setTopScrollRight] = useState(false);
  const [bottomScrollLeft, setBottomScrollLeft] = useState(false);
  const [bottomScrollRight, setBottomScrollRight] = useState(false);

  const { theme } = useTheme();
  const { t } = useTranslation();
  const { isRTL } = useI18n();

  const searchInputRef = useRef(null);
  const topContainerRef = useRef(null);
  const bottomContainerRef = useRef(null);

  const topScrollHandler = () => {
    const el = topContainerRef.current;
    if (!el) return;

    const { normalized, max } = getNormalizedScroll(el);
    const EPS = 1; // tolerance for fractional pixels

    setTopScrollLeft(normalized > EPS); // can scroll left
    setTopScrollRight(normalized < max - EPS); // can scroll right
  };

  const bottomScrollHandler = () => {
    const el = bottomContainerRef.current;
    if (!el) return;

    const { normalized, max } = getNormalizedScroll(el);
    const EPS = 1; // tolerance for fractional pixels

    setBottomScrollLeft(normalized > EPS); // can scroll left
    setBottomScrollRight(normalized < max - EPS); // can scroll right
  };

  useEffect(() => {
    const callBothScrollHandlers = () => {
      topScrollHandler();
      bottomScrollHandler();
    };

    const resizeScreen = () => {
      if (bottomContainerRef.current) {
        bottomScrollHandler();
      }
      if (topContainerRef.current) {
        topScrollHandler();
      }
    };

    resizeScreen();

    window.addEventListener("resize", resizeScreen);
    topContainerRef.current?.addEventListener("scroll", topScrollHandler);
    bottomContainerRef.current?.addEventListener("scroll", bottomScrollHandler);
    ref.current?.addEventListener("shown.bs.modal", callBothScrollHandlers);

    return () => {
      window.removeEventListener("resize", resizeScreen);
      topContainerRef.current?.removeEventListener("scroll", topScrollHandler);
      bottomContainerRef.current?.removeEventListener("scroll", bottomScrollHandler);
      ref.current?.removeEventListener("shown.bs.modal", callBothScrollHandlers);
    };
  }, []);

  useEffect(() => {
    if (showSearchInput && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [showSearchInput]);

  return (
    <>
      {showLabel ? (
        <label
          htmlFor='funds'
          className='form-label ps-1 mb-2 d-block text-center'
          style={{
            fontSize: "1.25rem",
            color: theme === "light" ? "rgba(0,0,0,0.6)" : "var(--theme-light-secondary)",
          }}
        >
          {title}
        </label>
      ) : ''}

      <div
        className=''
        style={{
          padding: "12px",
          border: "1px solid",
          backgroundColor: theme === 'dark' ? 'rgba(0,0,0,.15)' : "rgb(236, 238, 245)",
          borderColor: theme === "dark" ? "rgba(173, 158, 174, 0.4)" : "rgb(198, 203, 216)",
          borderRadius: "20px",
        }}
      >
        <div className='d-flex w-100 align-items-center flex-nowrap gap-2'>
          <div
            className='flex-grow-1 position-relative'
            style={{
              // maxWidth: 'calc(100% - 40px)',
              overflow: "hidden",
              minWidth: "0",
            }}
          >
            {topScrollLeft ? (
              <button
                className='btn border-0 m-0 position-absolute d-flex align-items-center justify-content-center'
                type='button'
                onClick={() => {
                  topContainerRef.current.scrollLeft -= 60;
                }}
                style={{
                  top: "-3px",
                  left: "3px",
                  color: "var(--theme-light-secondary)",
                  padding: "2px",
                  background: theme === "dark" ? "rgb(43, 43, 43)" : "rgb(236, 238, 245)",
                  boxShadow:
                    theme === "dark"
                      ? "rgb(43, 43, 43) 0px 0px 12px 14px, rgb(43, 43, 43) -13px 0px 4px 8px"
                      : "rgb(236, 238, 245) 0px 0px 12px 14px, rgb(236, 238, 245) -12px 0px 0px 6px",
                }}
              >
                <svg
                  xmlns='http://www.w3.org/2000/svg'
                  width={30}
                  height={30}
                  viewBox='0 0 24 24'
                  fill='none'
                  stroke='currentColor'
                  strokeWidth={2}
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  className='icon icon-tabler icons-tabler-outline icon-tabler-chevron-left'
                >
                  <path stroke='none' d='M0 0h24v24H0z' fill='none' />
                  <path d='M15 6l-6 6l6 6' />
                </svg>
              </button>
            ) : (
              ""
            )}

            {topScrollRight ? (
              <button
                className='btn border-0 m-0 position-absolute d-flex align-items-center justify-content-center'
                type='button'
                onClick={() => {
                  topContainerRef.current.scrollLeft += 60;
                }}
                style={{
                  top: "-3px",
                  right: "3px",
                  color: "var(--theme-light-secondary)",
                  padding: "2px",
                  background: theme === "dark" ? "rgb(43, 43, 43)" : "rgb(236, 238, 245)",
                  boxShadow:
                    theme === "dark"
                      ? "rgb(43, 43, 43) 0px 0px 12px 14px, rgb(43, 43, 43) 13px 0px 4px 8px"
                      : "rgb(236, 238, 245) 0px 0px 12px 14px, rgb(236, 238, 245) 13px 0px 4px 8px",
                }}
              >
                <svg
                  xmlns='http://www.w3.org/2000/svg'
                  width={30}
                  height={30}
                  viewBox='0 0 24 24'
                  fill='none'
                  stroke='currentColor'
                  strokeWidth={2}
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  className='icon icon-tabler icons-tabler-outline icon-tabler-chevron-right'
                >
                  <path stroke='none' d='M0 0h24v24H0z' fill='none' />
                  <path d='M9 6l6 6l-6 6' />
                </svg>
              </button>
            ) : (
              ""
            )}

            <div
              className='d-flex w-100 align-items-center flex-nowrap gap-2'
              ref={topContainerRef}
              style={{
                overflowX: "auto",
                scrollbarWidth: "none",
              }}
            >
              <button
                className={`btn${data.category?.name === "All" ? ' active' : ''}`}
                type='button'
                onClick={() => updateData((prev) => ({ ...prev, category: { name: "All" }, search: "" }))}
                style={{
                  paddingInline: "13px",
                  paddingBlock: "4.5px 4px",
                  borderRadius: "999px",
                  textWrap: "nowrap",
                  fontSize: "13px",
                  letterSpacing: ".04em",
                  // backgroundColor: data.category?.name === "All" ? "rgba(203, 110, 209, .6)" : "",
                  ...tabButton(theme),
                  // minWidth: '120px',
                  // maxWidth: '200px'
                }}
              >
                {t("requests.tabs.all")}
              </button>
              {filterItems?.map((category, idx) => (
                <button
                  className={`btn${data.category?.name === category?.name ? " active" : ""}`}
                  type='button'
                  key={idx}
                  onClick={() => {
                    updateData((prev) => ({ ...prev, category, search: "" }));

                    if (bottomContainerRef.current) {
                      bottomContainerRef.current.scrollLeft = 0;
                    }
                  }}
                  style={{
                    paddingInline: "13px",
                    paddingBlock: "4.5px 4px",
                    borderRadius: "999px",
                    textWrap: "nowrap",
                    fontSize: "13px",
                    letterSpacing: ".04em",
                    // backgroundColor: data.category?.name === category?.name ? "rgba(203, 110, 209, .6)" : "",
                    ...tabButton(theme),
                    // minWidth: '120px',
                    // maxWidth: '200px'
                  }}
                >
                  {category.name}
                </button>
              ))}
            </div>
          </div>
          <div className='flex-shrink-0 flex-grow-0'>
            {showSearchInput ? (
              <div
                className='position-relative'
                style={{
                  "--bs-border-color": "rgba(255,255,255,.4)",
                  "--bs-body-color": "rgba(255,255,255,.7)",
                  "--bs-body-bg": "rgba(255, 255, 255, 0)",
                }}
              >
                <input
                  type='text'
                  className='form-control investment-search'
                  placeholder={t("app.messages.search.glossary")}
                  value={data.search}
                  onChange={(e) => {
                    updateData((prev) => ({ ...prev, search: e.target.value }));
                  }}
                  onBlur={() => setShowSearchInput(false)}
                  aria-label='Search Term ...'
                  aria-describedby='button-addon2'
                  ref={searchInputRef}
                  style={{
                    minWidth: "260px",
                    color: theme === 'dark' ? "rgba(255,255,255,.7)" : "rgba(36, 36, 36, 0.7)",
                    borderRadius: "999px",
                    paddingInline: isRTL ? "2.3rem .75rem" : ".75rem 2.3rem",
                  }}
                />
                <button
                  className='btn h-100 border-0 position-absolute'
                  type='button'
                  id='button-addon2'
                  style={{
                    top: "0",
                    right: "0",
                    padding: ".25rem .5rem .45rem",
                  }}
                >
                  <svg
                    xmlns='http://www.w3.org/2000/svg'
                    width={22}
                    height={22}
                    viewBox='0 0 24 24'
                    fill='none'
                    stroke='var(--theme-light-secondary)'
                    strokeWidth={1.5}
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    className='icon icon-tabler icons-tabler-outline icon-tabler-search'
                  >
                    <path stroke='none' d='M0 0h24v24H0z' fill='none' />
                    <path d='M10 10m-7 0a7 7 0 1 0 14 0a7 7 0 1 0 -14 0' />
                    <path d='M21 21l-6 -6' />
                  </svg>
                </button>
              </div>
            ) : (
              <button
                className='btn h-100 border-0'
                type='button'
                id='button-addon2'
                onClick={() => {
                  setShowSearchInput(true);
                }}
                style={{
                  padding: ".25rem .5rem .45rem",
                }}
              >
                <svg
                  xmlns='http://www.w3.org/2000/svg'
                  width={22}
                  height={22}
                  viewBox='0 0 24 24'
                  fill='none'
                  stroke='var(--theme-light-secondary)'
                  strokeWidth={1.5}
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  className='icon icon-tabler icons-tabler-outline icon-tabler-search'
                >
                  <path stroke='none' d='M0 0h24v24H0z' fill='none' />
                  <path d='M10 10m-7 0a7 7 0 1 0 14 0a7 7 0 1 0 -14 0' />
                  <path d='M21 21l-6 -6' />
                </svg>
              </button>
            )}
          </div>
        </div>
        <div className=''>
          <hr className='mx-1' style={{ marginBlock: "12px", color: theme === "light" ? "rgb(33, 37, 41)" : "rgb(134, 151, 168)" }} />
        </div>
        <div className='position-relative overflow-hidden'>
          {bottomScrollLeft ? (
            <button
              className='btn border-0 m-0 position-absolute d-flex align-items-center justify-content-center'
              type='button'
              onClick={() => {
                bottomContainerRef.current.scrollLeft -= 60;
              }}
              style={{
                top: "-2px",
                bottom: "0px",
                left: "-8px",
                color: "var(--theme-light-secondary)",
                zIndex: "100",
                padding: "2px",
                background: theme === "dark" ? "rgb(43, 43, 43)" : "rgb(236, 238, 245)",
                boxShadow:
                  theme === "dark"
                    ? "rgb(43, 43, 43) 0px 0px 12px 14px, rgb(43, 43, 43) -13px 0px 4px 8px"
                    : "rgb(236, 238, 245) 0px 0px 12px 14px, rgb(236, 238, 245) -12px 0px 0px 6px",
              }}
            >
              <svg
                xmlns='http://www.w3.org/2000/svg'
                width={30}
                height={30}
                viewBox='0 0 24 24'
                fill='none'
                stroke='currentColor'
                strokeWidth={2}
                strokeLinecap='round'
                strokeLinejoin='round'
                className='icon icon-tabler icons-tabler-outline icon-tabler-chevron-left'
              >
                <path stroke='none' d='M0 0h24v24H0z' fill='none' />
                <path d='M15 6l-6 6l6 6' />
              </svg>
            </button>
          ) : (
            ""
          )}

          {bottomScrollRight ? (
            <button
              className='btn border-0 m-0 position-absolute d-flex align-items-center justify-content-center'
              type='button'
              onClick={() => {
                bottomContainerRef.current.scrollLeft += 60;
              }}
              style={{
                top: "-2px",
                bottom: "0px",
                right: "-8px",
                color: "var(--theme-light-secondary)",
                padding: "2px",
                zIndex: "100",
                background: theme === "dark" ? "rgb(43, 43, 43)" : "rgb(236, 238, 245)",
                boxShadow:
                  theme === "dark"
                    ? "rgb(43, 43, 43) 0px 0px 12px 14px, rgb(43, 43, 43) 13px 0px 4px 8px"
                    : "rgb(236, 238, 245) 0px 0px 12px 14px, rgb(236, 238, 245) 13px 0px 4px 8px",
              }}
            >
              <svg
                xmlns='http://www.w3.org/2000/svg'
                width={30}
                height={30}
                viewBox='0 0 24 24'
                fill='none'
                stroke='currentColor'
                strokeWidth={2}
                strokeLinecap='round'
                strokeLinejoin='round'
                className='icon icon-tabler icons-tabler-outline icon-tabler-chevron-right'
              >
                <path stroke='none' d='M0 0h24v24H0z' fill='none' />
                <path d='M9 6l6 6l-6 6' />
              </svg>
            </button>
          ) : (
            ""
          )}

          <div
            className='d-flex align-items-center flex-nowrap'
            ref={bottomContainerRef}
            style={{
              gap: "13px",
              overflowX: "auto",
              scrollbarWidth: "none",
            }}
          >
            {items?.map((item, idx) => {
              const price = separateNumberIfDecimal(item?.navData?.nav || "0", { format: true, digits: 4, method: 'round' });
              return (
                <button
                  className={`btn${data.fund?.symbol_code === item?.symbol_code ? " active" : ""}`}
                  type='button'
                  key={idx}
                  onClick={() => onItemSelect(item)}
                  style={{
                    padding: "10px 14px",
                    borderRadius: "12px",
                    width: "200px",
                    minWidth: "200px",
                    maxWidth: "200px",
                    backgroundColor: data.fund?.symbol_code === item?.symbol_code ? "rgba(203, 110, 209, .075)" : "",
                    borderColor: data.fund?.symbol_code === item?.symbol_code ? "rgba(203, 110, 209, .4)" : "",
                    ...fundButton(theme),
                  }}
                >
                  <div className='text-start position-relative' style={{ lineHeight: "1.1" }}>
                    <div
                      className=''
                      style={{
                        fontSize: "22px",
                        color: theme === "dark" ? "var(--theme-light-secondary)" : "",
                        marginBlockEnd: "2px",
                      }}
                    >
                      {item?.symbol_code || item?.fund_code}
                    </div>
                    <div
                      className='text-truncate'
                      data-tooltip-id={"fund-selector-fund-name"}
                      data-tooltip-content={item?.fund_name}
                      style={{
                        fontSize: "16px",
                        fontWeight: "300",
                        color: theme === "dark" ? "rgba(255,255,255,.6)" : "",
                      }}
                    >
                      {item?.fund_name || "-"}
                    </div>

                    <div className='position-absolute top-0 end-0'>
                      <div
                        className=''
                        dir='ltr'
                        style={{
                          fontSize: "12px",
                          color: theme === "dark" ? "rgba(255,255,255,.7)" : "",
                        }}
                      >
                        <span>@</span>
                        <span dir='ltr' style={{ marginInlineStart: "4px", color: theme === "dark" ? "" : "" }}>
                          {t("app.fund.rupeeAlt")}
                        </span>
                        <span dir='ltr' style={{ marginInlineStart: "4px", fontSize: "15px", color: theme === "dark" ? "var(--theme-light-secondary)" : "" }}>
                          {price.whole}
                          {price.hasDecimal ? "." : ""}
                          {price.hasDecimal ? price.decimal : ""}
                        </span>
                      </div>
                    </div>
                  </div>
                  {/* <div className=''>
                          <hr className='my-2 mx-1' style={{ color: theme === "light" ? "rgb(33, 37, 41)" : "rgb(134, 151, 168)" }} />
                          </div>
                          <div className="d-flex align-items-center justify-content-between flex-nowrap">
                          <div className="" style={{
                            fontSize: '14px',
                            color: theme === 'dark' ? 'rgba(255,255,255,.6)' : ''
                          }}>
                            Price:
                          </div>
                          <div className="" style={{
                            fontSize: '14px',
                            color: theme === 'dark' ? 'var(--theme-light-secondary)' : ''
                          }}>
                            {item?.navData?.nav || '0'}
                          </div>
                          </div> */}
                </button>
              )
            })}

          </div>
        </div>
            <Tooltip
              // anchorSelect={"asd"}
              id={"fund-selector-fund-name"}
              place='bottom'
              // offset={12}
              // delayShow={120}
              className='my-rt'
              classNameArrow='my-rt-arrow'
              border='1px solid rgba(255,255,255,0.12)'
              arrowSize={8}
              opacity={0.95}
            />
      </div>
    </>
  );
});

FundSelector.displayName = "FundSelector";

export default FundSelector;
